import React, { FC, useContext, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { motion } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ObjectSchema } from "yup";
import { CountryCode } from "libphonenumber-js";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { addServiceChatBookingResponse } from "../../Graphql/types/serviceChatBooking";
import { fetchGraphQL } from "../../Graphql/utils";
import { addServiceChatBookingQuery } from "../../Graphql/querries/serviceChatBooking";
import {
  cleanPhoneNumber,
  isValidPhoneNumberForCountry
} from "../../Utils/phoneValidatorUtil";
import { ServiceChatBookingFormData } from "./types";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import { AlertBannerCodeMap } from "../../Constants/AlertBannertCodeMap";
import {
  PhoneCountryCodeItem,
  phoneCountryCodeMap
} from "../../Constants/PhoneCountryCodeMap";

/** WhatsApp Business number. Set REACT_APP_WHATSAPP_PHONE_NUMBER in .env.local to override. */
const WHATSAPP_PHONE = (
  process.env.REACT_APP_WHATSAPP_PHONE_NUMBER || "250794004770"
).replace(/\D/g, "");
const WHATSAPP_URL = WHATSAPP_PHONE ? `https://wa.me/${WHATSAPP_PHONE}` : null;

const WhatsAppIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="currentColor"
    aria-hidden
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const ServiceChatBookingForm: FC = () => {
  const countryCode = process.env.REACT_APP_COUNTRY_CODE || "RW";
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { setAlert, alert } = useContext(AlertBannerContext);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [phoneCountryCode, setPhoneCountryCode] =
    useState<PhoneCountryCodeItem>(phoneCountryCodeMap[countryCode]);

  const schema: ObjectSchema<ServiceChatBookingFormData> = yup.object().shape({
    firstName: yup
      .string()
      .required("Firstname is required")
      .min(2, "Firstname must be at least 2 chars")
      .matches(/^[A-Za-z\s'-]+$/, "Special chars are not allowed"),
    lastName: yup
      .string()
      .required("Lastname is required")
      .min(2, "Lastname must be at least 3 chars")
      .matches(/^[A-Za-z\s'-]+$/, "Special chars are not allowed"),
    email: yup
      .string()
      .email("Please enter a valid email")
      .required("Email is required"),
    phoneNumber: yup
      .string()
      .required("PhoneNumber is required")
      .test("is-valid-phone", "Please enter a valid phone number", (value) =>
        value
          ? isValidPhoneNumberForCountry(
              value,
              phoneCountryCode.countryCode as CountryCode,
              phoneCountryCode.code
            )
          : false
      ),
    message: yup
      .string()
      .required("Message is required")
      .min(3, "Message must be at least 3 chars")
      .matches(/^[A-Za-z\s'-,.?!$():]+$/, "Special chars are not allowed")
  });

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<ServiceChatBookingFormData>({
    resolver: yupResolver(schema)
  });

  const { mutate, isPending } = useMutation<
    addServiceChatBookingResponse,
    Error,
    ServiceChatBookingFormData
  >({
    mutationKey: ["addServiceChatBooking"],
    mutationFn: (formData) =>
      fetchGraphQL(addServiceChatBookingQuery, { input: formData }, setAlert),
    onSuccess: () => {
      setShowSuccess(true);
    }
  });

  const handleClick = (form: ServiceChatBookingFormData) => {
    mutate({
      ...form,
      phoneNumber: cleanPhoneNumber(form.phoneNumber, phoneCountryCode.code)
    });
  };

  const handleCodeChange = (event: SelectChangeEvent) => {
    const config = phoneCountryCodeMap[event.target.value];
    setPhoneCountryCode(config);
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: "var(--color-surface-elevated)",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--color-primary)"
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "var(--color-primary)",
        borderWidth: 2
      }
    }
  };

  return (
    <Stack
      minHeight="150px"
      width="100%"
      maxWidth="100%"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      display="flex"
      spacing={3}
      sx={{ px: { xs: 2, sm: 0 }, boxSizing: "border-box" }}
    >
      <Box
        sx={{
          width: "min(560px, 100%)",
          maxWidth: "100%",
          mx: "auto",
          px: { xs: 0, sm: 0 },
          boxSizing: "border-box"
        }}
      >
        {showSuccess ? (
          <Stack
            spacing={2}
            justifyContent="center"
            alignItems="center"
            py={4}
            textAlign="center"
            width="100%"
          >
            <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
            <Typography variant="body1" color="text.secondary">
              Thank you, our team will reach out to you soon!
            </Typography>
          </Stack>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit(handleClick)}
            noValidate
            autoComplete="off"
            sx={{
              width: "100%",
              maxWidth: "100%",
              p: { xs: 2, sm: 3.5 },
              borderRadius: 3,
              boxShadow:
                "0 4px 24px rgba(0,0,0,0.06), 0 2px 8px rgba(0,0,0,0.04)",
              border: "1px solid var(--color-border)",
              backgroundColor: "var(--color-surface-elevated)",
              boxSizing: "border-box"
            }}
          >
            {isPending && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress sx={{ borderRadius: 1 }} />
              </Box>
            )}

            <Stack spacing={2.5}>
              <Stack
                direction={matchMD ? "column" : "row"}
                gap={2}
                sx={{ width: "100%" }}
              >
                <Controller
                  name="firstName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      error={!!errors.firstName}
                      size="medium"
                      label="First name"
                      variant="outlined"
                      helperText={errors.firstName?.message}
                      fullWidth
                      sx={fieldSx}
                    />
                  )}
                />
                <Controller
                  name="lastName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      error={!!errors.lastName}
                      size="medium"
                      label="Last name"
                      variant="outlined"
                      helperText={errors.lastName?.message}
                      fullWidth
                      sx={fieldSx}
                    />
                  )}
                />
              </Stack>

              <Controller
                name="email"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.email}
                    size="medium"
                    label="Email"
                    variant="outlined"
                    helperText={errors.email?.message}
                    type="email"
                    fullWidth
                    sx={fieldSx}
                  />
                )}
              />

              <Stack gap={1.5} width="100%">
                <FormControl
                  size="medium"
                  fullWidth
                  error={!!errors.phoneNumber}
                  sx={fieldSx}
                >
                  <InputLabel id="phone-country-code-select-label">
                    Country code
                  </InputLabel>
                  <Select
                    labelId="phone-country-code-select-label"
                    id="phone-country-code-select"
                    value={phoneCountryCode.countryCode}
                    onChange={handleCodeChange}
                    label="Country code"
                    sx={{ borderRadius: 2 }}
                  >
                    {Object.keys(phoneCountryCodeMap).map((country) => (
                      <MenuItem
                        key={phoneCountryCodeMap[country].code}
                        value={country}
                      >
                        {`${phoneCountryCodeMap[country].flag} (${phoneCountryCodeMap[country].code})`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Controller
                  name="phoneNumber"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      error={!!errors.phoneNumber}
                      size="medium"
                      label="Phone number"
                      variant="outlined"
                      type="tel"
                      fullWidth
                      helperText={errors.phoneNumber?.message}
                      sx={fieldSx}
                    />
                  )}
                />
              </Stack>

              <Controller
                name="message"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.message}
                    label="Message"
                    variant="outlined"
                    multiline
                    minRows={4}
                    maxRows={8}
                    fullWidth
                    helperText={errors.message?.message}
                    sx={{
                      ...fieldSx,
                      "& .MuiInputBase-root": { alignItems: "flex-start" }
                    }}
                  />
                )}
              />
            </Stack>

            {alert && (
              <FormHelperText sx={{ color: "error.main", mt: 1 }}>
                {AlertBannerCodeMap[alert.code] ||
                  AlertBannerCodeMap.INTERNAL_SERVER_ERROR}
              </FormHelperText>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              flexWrap="wrap"
              gap={2}
              justifyContent="center"
              alignItems="stretch"
              sx={{ mt: 3 }}
            >
              <Button
                variant="outlined"
                type="submit"
                size="large"
                component={motion.button}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  px: 3,
                  borderRadius: 2,
                  borderColor: "var(--color-primary)",
                  color: "var(--color-primary)",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "var(--color-primary-light)",
                    backgroundColor: "var(--color-primary-glow)"
                  }
                }}
              >
                Send it...
              </Button>
              {WHATSAPP_URL && (
                <Button
                  component={motion.a}
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  size="large"
                  startIcon={<WhatsAppIcon />}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    px: 3,
                    borderRadius: 2,
                    color: "#25D366",
                    borderColor: "#25D366",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: "#20BD5A",
                      backgroundColor: "rgba(37, 211, 102, 0.08)"
                    }
                  }}
                >
                  Chat on WhatsApp
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default ServiceChatBookingForm;
